import { useEffect, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';

import { API_SUBS_ME, SUB_REQUIRED, VIEW_SUBSCRIBE } from './EsConstants';
import { esRead } from './EsFetch';
import { esFormatDollars } from './EsIntl';
import { useAuth } from '../views/parent/EsAuth';
import { esLiteral, useEsStrings } from './EsStrings';

/**
 * The reader's subscription, as subs/me answers it, and the arithmetic every
 * screen shows about it.
 *
 * <p>ONE store, because four places read it -- the nav's Subscribe entry, the
 * Subscribe page, its return page, and the account page -- and a payment has to
 * change all of them at once. The return page's refresh is what tells them.
 */
const EMPTY = { me: null, loaded: false, error: null };

let gState = EMPTY;
let gInFlight = null;
const gWatchers = new Set();

const setState = ( pNext ) => {
    gState = pNext;
    gWatchers.forEach( ( pNotify ) => pNotify() );
};

const subscribe = ( pNotify ) => {
    gWatchers.add( pNotify );
    return () => gWatchers.delete( pNotify );
};

const snapshot = () => gState;

/** Read subs/me again, and tell every reader. One request at a time. */
export function esSubsRefresh() {
    if ( gInFlight ) return gInFlight;

    gInFlight = esRead( API_SUBS_ME )
        .then( ( pMe ) => setState( { me: pMe, loaded: true, error: null } ) )
        .catch( ( pError ) => setState( { me: null, loaded: true, error: pError.message } ) )
        .finally( () => { gInFlight = null; } );

    return gInFlight;
}

/** Tests only: forget what was read. */
export function esSubsReset() {
    gInFlight = null;
    setState( EMPTY );
}

/**
 * { me, loaded, error }. Reads nothing unless enabled -- the flag on and
 * somebody logged in -- because subs/me is a 404 with the flag off and a 401
 * logged out, and neither is worth a request.
 */
export function useEsSubMe( pEnabled ) {
    const aState = useSyncExternalStore( subscribe, snapshot, snapshot );

    useEffect( () => {
        if ( pEnabled && !aState.loaded ) esSubsRefresh();
    }, [ pEnabled, aState.loaded ] );

    return pEnabled ? aState : EMPTY;
}

// ---- the arithmetic -------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/** Reasons that never pay: admin, alpha, and beta are free for life by tier, not by date. */
export const FREE_REASONS = [ 'lifetime' ];

export const REASON_TEXT = {
    lifetime: 'Lifetime',
    trial: 'Free trial',
    paid: 'Paid',
    lapsed: 'Lapsed',
};

/** How a renewal ledger row came about. */
export const KIND_TEXT = { trial: 'Free trial', stripe: 'Card', admin: 'Administrator', refund: 'Refund' };

/** REASON_TEXT in the reader's locale, for the account's own screens; the admin list keeps the table. */
export function esReasonText( pReason, t = esLiteral ) {
    switch ( pReason ) {
        case 'lifetime': return t( 'subscribe-reason-lifetime', 'Lifetime' );
        case 'trial': return t( 'subscribe-reason-trial', 'Free trial' );
        case 'paid': return t( 'subscribe-reason-paid', 'Paid' );
        case 'lapsed': return t( 'subscribe-reason-lapsed', 'Lapsed' );
        default: return pReason;
    }
}

const toDate = ( pValue ) => {
    if ( !pValue ) return null;
    const aDate = pValue instanceof Date ? pValue : new Date( pValue );
    return Number.isNaN( aDate.getTime() ) ? null : aDate;
};

/**
 * Where a renewal counts from: the current end while it is still ahead, today
 * once it has passed. esback's rule, so the date promised here is the date it
 * writes.
 */
export function esRenewalBase( pSubEnd, pNow = new Date() ) {
    const aEnd = toDate( pSubEnd );
    return aEnd && aEnd > pNow ? aEnd : pNow;
}

/**
 * A year after esRenewalBase. 29 February lands on 28 February, as Java's
 * plusYears does, rather than rolling into March the way setFullYear would.
 */
export function esRenewalEnd( pSubEnd, pNow = new Date() ) {
    const aBase = esRenewalBase( pSubEnd, pNow );
    const aNext = new Date( aBase.getTime() );

    aNext.setFullYear( aBase.getFullYear() + 1 );
    if ( aNext.getMonth() !== aBase.getMonth() ) aNext.setDate( 0 );

    return aNext;
}

/**
 * Whether anything but the trial ever extended the sub: esback's test for PAID over TRIAL,
 * read off the ledger. A payment from an account that never paid is its first, so the
 * screens say Subscribe to it and Renew only to one that has.
 */
export function esHasPaid( pMe ) {
    return ( pMe?.renewals ?? [] ).some( ( pRenewal ) => pRenewal.kind !== 'trial' );
}

/** Whole days until the end, rounded up; 0 once it has passed. */
export function esDaysLeft( pSubEnd, pNow = new Date() ) {
    const aEnd = toDate( pSubEnd );
    if ( !aEnd ) return 0;
    return Math.max( 0, Math.ceil( ( aEnd.getTime() - pNow.getTime() ) / DAY_MS ) );
}

/** "$35" from 3500, and "$37.10" from 3710, in the reader's locale: cents only when there are some. */
export function esPrice( pCents, pTag ) {
    return esFormatDollars( pCents ?? 3500, pTag );
}

/**
 * Whether a trial's clock has yet to start: no clock starts before the
 * release, so an older account's trial begins on esback's trialStart.
 */
export function esTrialPending( pMe, pNow = new Date() ) {
    const aStart = toDate( pMe?.trialStart );
    return pMe?.reason === 'trial' && aStart != null && aStart > pNow;
}

// ---- the 402 ---------------------------------------------------------------

/**
 * Whether the reader has maker privileges: an admin, alpha, or beta, an account in its free
 * trial, or one paid up. True until esback says otherwise, so nothing flickers away while
 * the account loads; esback refuses the write whatever this says.
 */
export function useEsMaker() {
    return useAuth()?.account?.maker !== false;
}

/** Whether a refused write was esback saying "this needs maker privileges". */
export function esIsSubRequired( pError ) {
    return pError?.status === 402 || pError?.body?.code === SUB_REQUIRED;
}

/** The refusal as one line with the way out in it. */
export function EsSubRequired( { what } ) {
    const t = useEsStrings();
    return (
        <span data-testid="es-subscribe-required">
            { t( 'subscribe-required-line', '{what} needs maker privileges. Players are always free. {subscribe}', { what, subscribe:
                <Link to={ VIEW_SUBSCRIBE } data-testid="es-subscribe-required-link"
                      className="text-accent hover:underline">
                    { t( 'subscribe-required-link', 'Subscribe' ) }
                </Link> } ) }
        </span>
    );
}

/**
 * What a write's catch puts on screen: the subscription line for a 402, and
 * esback's own message for anything else.
 */
export function esRefusalOf( pError, pWhat ) {
    return esIsSubRequired( pError ) ? <EsSubRequired what={ pWhat } /> : pError.message;
}
